import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Zap, RefreshCw, TrendingUp, TrendingDown, Minus, AlertTriangle, ShieldAlert, ShieldCheck, Gauge } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';

interface TimeSeriesEntry {
  date: string;
  openai: number;
  elevenlabs: number;
  claude: number;
  google: number;
  openai_cost: number;
  elevenlabs_cost: number;
  claude_cost: number;
  google_cost: number;
}

interface ProviderTotal {
  total_tokens?: number;
  request_count?: number;
  estimated_cost?: number;
  real_cost?: number;
  total_conversations?: number;
  total_duration_seconds?: number;
}

interface GrandTotal {
  total_tokens: number;
  total_requests: number;
  total_cost: number;
  total_cost_real?: number;
  elevenlabs_conversations: number;
  elevenlabs_duration: number;
}

interface ModelBreakdownEntry {
  model: string;
  provider?: string;
  pricing_unknown?: boolean;
  estimated_cost?: number;
  real_cost?: number;
}

interface CostSourceStatus {
  provider: 'openai';
  source_default: 'real' | 'estimated';
  admin_key_configured: boolean;
  project_key_configured: boolean;
  last_sync_at: string | null;
  last_sync_ok: boolean;
  last_error: string | null;
  message: string;
}

interface EnhancedData {
  period: string;
  days: number;
  time_series: TimeSeriesEntry[];
  provider_totals: Record<string, ProviderTotal>;
  grand_total: GrandTotal;
  model_breakdown?: ModelBreakdownEntry[];
}

interface AIConsumption {
  daily_tokens_used: number;
  daily_token_limit: number;
  daily_token_pct: number;
  monthly_cost_used: number;
  monthly_cost_limit: number;
  monthly_cost_pct: number;
  rag_queries_last_hour: number;
  rag_queries_per_hour: number;
  rag_queries_pct: number;
  overall_pct: number;
  status: string;
  quota_enabled: boolean;
}

type ActiveView = 'overview' | 'providers' | 'quota';

function formatTokens(n: number): string {
  const v = typeof n === 'number' && !isNaN(n) ? n : 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toString();
}

function formatCost(n: number): string {
  const v = typeof n === 'number' && !isNaN(n) ? n : 0;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K €`;
  return `${v.toFixed(2)} €`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ${Math.round(seconds % 60)}s`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

function MiniSparkline({ data, color }: { data: number[]; color: string }) {
  if (data.length === 0) return null;
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - (v / max) * 100;
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-8">
      <defs>
        <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,100 ${points} 100,100`}
        fill={`url(#grad-${color})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function QuotaRing({ pct, size = 52, strokeWidth = 5 }: { pct: number; size?: number; strokeWidth?: number }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const offset = circumference - (clamped / 100) * circumference;

  let color = '#22c55e';
  if (clamped >= 95) color = '#ef4444';
  else if (clamped >= 80) color = '#f59e0b';
  else if (clamped >= 60) color = '#3b82f6';

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-gray-200 dark:text-gray-700"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
}

function QuotaBar({ pct, label, used, limit }: { pct: number; label: string; used: string; limit: string }) {
  const clamped = Math.min(pct, 100);
  let barClass = 'bg-emerald-500';
  if (pct >= 95) barClass = 'bg-red-500';
  else if (pct >= 80) barClass = 'bg-amber-500';
  else if (pct >= 60) barClass = 'bg-blue-500';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
          <span className="font-semibold text-gray-800 dark:text-gray-200">{used}</span>
          <span className="mx-0.5">/</span>
          {limit}
        </span>
      </div>
      <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-700 ease-out", barClass)}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}

const PROVIDER_CONFIG: Record<string, { label: string; color: string; dotClass: string }> = {
  openai: { label: 'OpenAI', color: '#22c55e', dotClass: 'bg-green-500' },
  claude: { label: 'Claude', color: '#a855f7', dotClass: 'bg-purple-500' },
  google: { label: 'Gemini', color: '#3b82f6', dotClass: 'bg-blue-500' },
  elevenlabs: { label: 'ElevenLabs', color: '#8b5cf6', dotClass: 'bg-violet-500' },
};

export default function TokenUsageWidget() {
  const { tenantSlug } = useParams();
  const [data, setData] = useState<EnhancedData | null>(null);
  const [quota, setQuota] = useState<AIConsumption | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<ActiveView>('overview');
  const [open, setOpen] = useState(false);
  // Nessun default ottimistico: il badge resta neutro finché /cost-source-status
  // (l'unica verità, tenant-aware) non risponde. Evita di mostrare "reali" su
  // tenant che potrebbero non averli.
  const [costSource, setCostSource] = useState<CostSourceStatus | null>(null);
  // Race-guard: scarta risposte out-of-order quando interval e refresh manuale si sovrappongono.
  const loadSeqRef = useRef(0);

  const loadData = useCallback(async () => {
    if (!tenantSlug) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const [analyticsRes, quotaRes, costSrcRes] = await Promise.all([
        apiClient.get('/ai-tools/analytics/enhanced?period=7d&provider=all').catch(() => null),
        apiClient.get('/ai-quota/consumption').catch(() => null),
        apiClient.get('/ai-tools/cost-source-status').catch(() => null),
      ]);
      if (seq !== loadSeqRef.current) return; // risposta superata, ignora
      if (analyticsRes?.data) setData(analyticsRes.data as EnhancedData);
      if (quotaRes?.data) setQuota(quotaRes.data as AIConsumption);
      if (costSrcRes?.data) {
        setCostSource(costSrcRes.data as CostSourceStatus);
      } else {
        // Endpoint irraggiungibile: stato conservativo, non mentire mostrando "reali".
        setCostSource((prev) => ({
          provider: 'openai',
          admin_key_configured: prev?.admin_key_configured ?? false,
          project_key_configured: prev?.project_key_configured ?? false,
          last_sync_at: prev?.last_sync_at ?? null,
          source_default: 'estimated',
          last_sync_ok: false,
          last_error: 'unreachable',
          message: 'Stato costi OpenAI non verificabile (servizio non raggiungibile).',
        }));
      }
    } catch (err) {
      console.error('Token usage load error:', err);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 300000);
    return () => clearInterval(interval);
  }, [loadData]);

  const pt = useMemo(() => data?.provider_totals || {}, [data?.provider_totals]);
  const gt = data?.grand_total;

  const activeProviders = useMemo(() => {
    return Object.entries(PROVIDER_CONFIG).filter(([key]) => {
      if (key === 'elevenlabs') return (pt[key]?.total_conversations || 0) > 0;
      return (pt[key]?.total_tokens || 0) > 0;
    });
  }, [pt]);

  const dailySeries = useMemo(() => {
    if (!data?.time_series) return [];
    return data.time_series.slice(-7).map(entry => ({
      date: entry.date,
      day: new Date(entry.date).toLocaleDateString('it-IT', { weekday: 'short' }).slice(0, 2),
      total: entry.openai + entry.elevenlabs + (entry.claude || 0) + (entry.google || 0),
      cost: entry.openai_cost + entry.elevenlabs_cost + (entry.claude_cost || 0) + (entry.google_cost || 0),
    }));
  }, [data]);

  const trend = useMemo(() => {
    if (dailySeries.length < 2) return 0;
    const recent = dailySeries.slice(-3).reduce((s, d) => s + d.total, 0) / 3;
    const older = dailySeries.slice(0, -3).reduce((s, d) => s + d.total, 0) / Math.max(dailySeries.length - 3, 1);
    if (older === 0) return recent > 0 ? 100 : 0;
    return Math.round(((recent - older) / older) * 100);
  }, [dailySeries]);

  const partialPricing = useMemo(() => {
    const mb = data?.model_breakdown;
    if (!Array.isArray(mb) || mb.length === 0) return { has: false, count: 0 };
    const unknown = mb.filter(m => m?.pricing_unknown);
    return { has: unknown.length > 0, count: unknown.length };
  }, [data?.model_breakdown]);

  const TrendIcon = trend > 5 ? TrendingUp : trend < -5 ? TrendingDown : Minus;
  const trendColor = trend > 5 ? 'text-red-500' : trend < -5 ? 'text-emerald-500' : 'text-gray-400';

  const quotaStatus = quota?.quota_enabled ? quota.status : null;
  const hasQuota = quota?.quota_enabled === true;

  const headerIndicatorColor = (() => {
    if (quotaStatus === 'blocked') return 'bg-red-500';
    if (quotaStatus === 'throttled') return 'bg-amber-500';
    if (quotaStatus === 'alert') return 'bg-yellow-400';
    return '';
  })();

  const headerBadge = (() => {
    if (!gt) return null;
    if (gt.total_tokens > 0) return formatTokens(gt.total_tokens);
    return null;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "relative hover:bg-white/50 dark:hover:bg-white/10 hover:scale-105 transition-all duration-200 p-2 gap-1.5",
            open && "bg-purple-50 dark:bg-purple-900/30"
          )}
          title="Utilizzo AI"
        >
          <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          {headerBadge && (
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300 hidden lg:inline">
              {headerBadge}
            </span>
          )}
          {headerIndicatorColor && (
            <span className={cn("absolute top-1 right-1 h-2 w-2 rounded-full animate-pulse", headerIndicatorColor)} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0 shadow-xl border-gray-200 dark:border-gray-700" align="end">
        <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 p-4 rounded-t-lg">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="bg-white/20 rounded-lg p-1.5">
                <Zap className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-sm text-white">AI Analytics</h3>
                <p className="text-[10px] text-purple-200">Ultimi 7 giorni</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-white/70 hover:text-white hover:bg-white/10"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
            </Button>
          </div>

          {(costSource === null || costSource.source_default !== 'real') && (
            <div
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded-md mb-3 text-[10px] font-medium border",
                costSource === null
                  ? "bg-white/10 border-white/20 text-purple-100"
                  : "bg-amber-500/20 border-amber-300/40 text-amber-100"
              )}
              title={
                costSource === null
                  ? 'Verifica stato costi OpenAI in corso…'
                  : costSource.last_sync_at
                    ? `${costSource.message}\nUltima sync: ${new Date(costSource.last_sync_at).toLocaleString('it-IT')}`
                    : costSource.message
              }
            >
              {costSource === null ? (
                <RefreshCw className="h-3 w-3 shrink-0 animate-spin" />
              ) : (
                <AlertTriangle className="h-3 w-3 shrink-0" />
              )}
              <span className="truncate">
                {costSource === null
                  ? 'Costi OpenAI · verifica in corso…'
                  : !costSource.admin_key_configured && !costSource.project_key_configured
                    ? 'Costi OpenAI stimati · chiave non configurata'
                    : costSource.last_error
                      ? costSource.last_error === 'unreachable'
                        ? 'Costi OpenAI stimati · stato non verificabile'
                        : 'Costi OpenAI stimati · recupero in errore'
                      : 'Costi OpenAI stimati · recupero in corso'}
              </span>
            </div>
          )}

          {gt && (
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
                <div className="text-base font-bold text-white tabular-nums">{formatTokens(gt.total_tokens)}</div>
                <div className="text-[9px] text-purple-200 uppercase tracking-wider">Token</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-base font-bold text-white tabular-nums">{formatCost(gt.total_cost_real ?? gt.total_cost)}</span>
                  {gt.total_cost_real != null && (
                    <span className="text-[8px] font-semibold text-emerald-200 bg-emerald-500/20 px-1 rounded">Reale</span>
                  )}
                </div>
                <div className="text-[9px] text-purple-200 uppercase tracking-wider">Costo</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-base font-bold text-white tabular-nums">{gt.total_requests}</span>
                  <TrendIcon className={cn("h-3 w-3", trend > 5 ? "text-red-300" : trend < -5 ? "text-emerald-300" : "text-purple-300")} />
                </div>
                <div className="text-[9px] text-purple-200 uppercase tracking-wider">Richieste</div>
              </div>
            </div>
          )}
        </div>

        <div className="px-3 pt-2 pb-1">
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            {([
              { id: 'overview' as ActiveView, label: 'Panoramica' },
              { id: 'providers' as ActiveView, label: 'Provider' },
              ...(hasQuota ? [{ id: 'quota' as ActiveView, label: 'Quota' }] : []),
            ]).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={cn(
                  "flex-1 px-2 py-1.5 text-[11px] font-medium rounded-md transition-all",
                  activeView === tab.id
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-3 min-h-[140px]">
          {loading && !data ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-200 border-t-purple-600" />
            </div>
          ) : activeView === 'overview' ? (
            <div className="space-y-3">
              {partialPricing.has && (
                <div
                  className="flex items-center gap-1.5 text-[10px] font-medium text-amber-700 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 px-2 py-1 rounded-md"
                  title={`${partialPricing.count} modello/i senza prezzo nel catalogo (llm_model_pricing). Il costo mostrato esclude questi modelli.`}
                >
                  <AlertTriangle className="h-3 w-3" />
                  <span>Prezzi parziali ({partialPricing.count})</span>
                </div>
              )}
              {dailySeries.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Andamento token</span>
                    <div className={cn("flex items-center gap-0.5 text-[10px] font-medium", trendColor)}>
                      <TrendIcon className="h-3 w-3" />
                      {Math.abs(trend)}%
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2 overflow-hidden">
                    <MiniSparkline data={dailySeries.map(d => d.total)} color="#9333ea" />
                    <div className="flex justify-between mt-1">
                      {dailySeries.map((d, i) => (
                        <span key={i} className="text-[8px] text-gray-400 tabular-nums">{d.day}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeProviders.length > 0 && (
                <div className="space-y-1.5">
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Provider attivi</span>
                  {activeProviders.map(([key, config]) => {
                    const p = pt[key];
                    const isEL = key === 'elevenlabs';
                    const value = isEL
                      ? `${p?.total_conversations || 0} conv`
                      : formatTokens(p?.total_tokens || 0);
                    const cost = p?.real_cost ?? p?.estimated_cost ?? 0;

                    return (
                      <div key={key} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2">
                          <div className={cn("w-2 h-2 rounded-full", config.dotClass)} />
                          <span className="text-xs text-gray-700 dark:text-gray-300">{config.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">{value}</span>
                          <span className="text-[10px] text-gray-400 tabular-nums">{formatCost(cost)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {hasQuota && quota && (
                <div
                  className={cn(
                    "flex items-center gap-2.5 p-2 rounded-lg cursor-pointer transition-colors",
                    quotaStatus === 'blocked' ? "bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50" :
                    quotaStatus === 'throttled' ? "bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50" :
                    quotaStatus === 'alert' ? "bg-yellow-50 dark:bg-yellow-950/30 hover:bg-yellow-100 dark:hover:bg-yellow-950/50" :
                    "bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800"
                  )}
                  onClick={() => setActiveView('quota')}
                >
                  <QuotaRing pct={quota.overall_pct} size={36} strokeWidth={4} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                        Quota AI: {(typeof quota.overall_pct === 'number' ? quota.overall_pct : 0).toFixed(0)}%
                      </span>
                      {quotaStatus === 'blocked' && <ShieldAlert className="h-3.5 w-3.5 text-red-500" />}
                      {quotaStatus === 'throttled' && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                      {quotaStatus === 'alert' && <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />}
                      {quotaStatus === 'active' && <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />}
                    </div>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                      {quotaStatus === 'blocked' ? 'Richieste AI bloccate'
                        : quotaStatus === 'throttled' ? 'Richieste rallentate'
                        : quotaStatus === 'alert' ? 'Quota in esaurimento'
                        : 'Quota nella norma'}
                    </p>
                  </div>
                  <span className="text-gray-400 text-xs">&rsaquo;</span>
                </div>
              )}

              {!gt && !hasQuota && (
                <div className="text-center py-6 text-sm text-gray-400">
                  Nessun dato AI disponibile
                </div>
              )}
            </div>
          ) : activeView === 'providers' ? (
            <div className="space-y-3">
              {activeProviders.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-400">
                  Nessun provider attivo negli ultimi 7 giorni
                </div>
              ) : (
                activeProviders.map(([key, config]) => {
                  const p = pt[key];
                  const isEL = key === 'elevenlabs';
                  const seriesData = data?.time_series?.slice(-7).map(e => (e[key as keyof TimeSeriesEntry] as number) || 0) || [];

                  return (
                    <div key={key} className="bg-gray-50 dark:bg-gray-800/40 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <div className={cn("w-2.5 h-2.5 rounded-full", config.dotClass)} />
                          <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{config.label}</span>
                        </div>
                        <span className="text-xs font-bold text-gray-900 dark:text-white tabular-nums">
                          {formatCost(p?.estimated_cost || 0)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-1.5">
                        <div>
                          <div className="text-[10px] text-gray-500">{isEL ? 'Conversazioni' : 'Token'}</div>
                          <div className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                            {isEL ? (p?.total_conversations || 0) : formatTokens(p?.total_tokens || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500">{isEL ? 'Durata' : 'Richieste'}</div>
                          <div className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                            {isEL ? formatDuration(p?.total_duration_seconds || 0) : (p?.request_count || 0)}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-500">Costo</div>
                          <div className="text-xs font-semibold text-gray-900 dark:text-white tabular-nums">
                            {formatCost(p?.estimated_cost || 0)}
                          </div>
                        </div>
                      </div>
                      {seriesData.length > 0 && (
                        <MiniSparkline data={seriesData} color={config.color} />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : activeView === 'quota' && hasQuota && quota ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <QuotaRing pct={quota.overall_pct} size={56} strokeWidth={5} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200 tabular-nums">
                      {(typeof quota.overall_pct === 'number' ? quota.overall_pct : 0).toFixed(0)}%
                    </span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Gauge className="h-3.5 w-3.5 text-purple-500" />
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">Stato Quota</span>
                  </div>
                  {quotaStatus === 'active' && (
                    <div className="flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3 text-emerald-500" />
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Nella norma</span>
                    </div>
                  )}
                  {quotaStatus === 'alert' && (
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 text-yellow-500" />
                      <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Attenzione &mdash; quota in esaurimento</span>
                    </div>
                  )}
                  {quotaStatus === 'throttled' && (
                    <div className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3 text-amber-500" />
                      <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Rallentato &mdash; quasi al limite</span>
                    </div>
                  )}
                  {quotaStatus === 'blocked' && (
                    <div className="flex items-center gap-1">
                      <ShieldAlert className="h-3 w-3 text-red-500" />
                      <span className="text-xs text-red-600 dark:text-red-400 font-medium">Bloccato &mdash; quota esaurita</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2.5">
                <QuotaBar
                  pct={quota.daily_token_pct}
                  label="Token giornalieri"
                  used={formatTokens(quota.daily_tokens_used)}
                  limit={formatTokens(quota.daily_token_limit)}
                />
                <QuotaBar
                  pct={quota.monthly_cost_pct}
                  label="Costo mensile"
                  used={formatCost(quota.monthly_cost_used)}
                  limit={formatCost(quota.monthly_cost_limit)}
                />
                <QuotaBar
                  pct={quota.rag_queries_pct}
                  label="Query RAG / ora"
                  used={quota.rag_queries_last_hour.toString()}
                  limit={quota.rag_queries_per_hour.toString()}
                />
              </div>

              {(quotaStatus === 'alert' || quotaStatus === 'throttled' || quotaStatus === 'blocked') && (
                <div className={cn(
                  "p-2 rounded-lg text-[11px] leading-relaxed",
                  quotaStatus === 'blocked' ? "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300" :
                  quotaStatus === 'throttled' ? "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300" :
                  "bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-300"
                )}>
                  {quotaStatus === 'blocked'
                    ? 'Le richieste AI sono bloccate. Contatta l\'amministratore per aumentare la quota.'
                    : quotaStatus === 'throttled'
                    ? 'Le richieste AI vengono rallentate per evitare il superamento della quota.'
                    : 'Hai raggiunto l\'80% della quota. Monitora il consumo per evitare interruzioni.'}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
